import {
    Box,
    InputAdornment,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    IconButton,
} from '@mui/material';
import { ChevronDown, ChevronRight, Search } from 'lucide-react';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useRecoilValue } from 'recoil';
import { useDebounceValue } from 'usehooks-ts';
import CopyField from '~/components/Fields/CopyField';
import SectionLabel from '~/components/SectionLabel';
import { DcmJsDatasetWrapper } from '~/domain/dicom/dcmJsWrapper';
import { showSensitiveInfoState } from '~/state/atoms/viewerAtoms';
import {
    DicomTag,
    extractDicomTags,
    flattenAllTags,
    flattenVisibleTags,
    hasLiteralValue,
    isPatientInformationTag,
} from '~/utils/dicomTags';
import DicomTagValueDetail from './DicomTagValueDetail';
import {
    CELL_PADDING_X_PX,
    NAME_COLUMN_WIDTH_PX,
    TABLE_MIN_WIDTH_PX,
    TAG_COLUMN_WIDTH_PX,
    VR_COLUMN_WIDTH_PX,
    indentForDepth,
    monospaceSx,
} from './dicomTagLayout';
import { TAG_ROW_HEIGHT_PX, useVirtualRows } from './dicomTagVirtualRows';

interface DicomTagBrowserProps {
    dataset: DcmJsDatasetWrapper | null;
}

/**
 * A query that matches no tag name has to be compared against every tag
 * value, and a structure set holds hundreds of megabytes of them: tens of
 * milliseconds per keystroke. Filtering waits for a pause in typing.
 */
const SEARCH_DEBOUNCE_MS = 300;

/**
 * The root every UID the DICOM standard itself defines starts with (PS3.5
 * section 9.1): SOP classes, transfer syntaxes, well-known instances. A UID
 * under any other root was minted for one object and links records the
 * way an identifier does.
 */
const DICOM_STANDARD_UID_ROOT = '1.2.840.10008.';

/** A UID that names an instance, study, series or frame, not a class. */
function isInstanceUid(tag: DicomTag): boolean {
    return tag.vr === 'UI' && !tag.value.startsWith(DICOM_STANDARD_UID_ROOT);
}

const headerCellSx = {
    fontSize: 12,
    position: 'sticky' as const,
    bgcolor: 'background.paper',
    borderColor: 'divider',
    zIndex: 1,
} as const;

// Cells drop the theme's vertical padding, which is what lets the row's own
// TAG_ROW_HEIGHT_PX hold: nothing a row contains (a line of clipped text, or
// the 22px sequence toggle) reaches that height on its own, so the row is
// exactly as tall as it is told to be, which is what the virtualization
// assumes when it maps a scroll offset to a row index.
const cellSx = {
    py: 0,
    fontSize: 12,
    borderColor: 'divider',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
} as const;

/**
 * Cells of an indented row: the rule is drawn by the row instead, inset to
 * the indent so it reads as belonging to the nested block.
 */
const borderlessCellSx = { ...cellSx, border: 'none' } as const;

/** The value inside a cell that also holds a copy button. */
const clippedValueSx = {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
} as const;

/** Applied over a value cell whose tag carries PHI. */
const redactedSx = { filter: 'blur(0.5rem)', userSelect: 'none' } as const;

// The group separator's eyebrow, dimmer than a section's and on one line.
const separatorLabelSx = {
    color: 'text.disabled',
    whiteSpace: 'nowrap',
    pl: 1,
} as const;

/**
 * Stands in for the rows outside the rendered window so the scrollbar keeps
 * the length it would have if every row were mounted. The height is set
 * inline rather than through `sx`, which would mint an emotion class per
 * distinct scroll offset.
 *
 * Hidden from assistive technology: it carries no data, and leaving it in the
 * tree would insert two empty rows into a table whose row numbering is
 * already stated by `aria-rowcount` and `aria-rowindex`.
 */
function SpacerRow({ heightPx }: { heightPx: number }) {
    if (heightPx <= 0) {
        return null;
    }

    return (
        <TableRow aria-hidden style={{ height: `${heightPx}px` }}>
            <TableCell colSpan={4} sx={{ p: 0, border: 'none' }} />
        </TableRow>
    );
}

/**
 * Whether a row's value is masked: Safe Mode is on, and the value is patient
 * information or a UID minted for one object.
 */
function isRedactedTag(tag: DicomTag, showSensitiveInfo: boolean): boolean {
    return (
        !showSensitiveInfo &&
        (isPatientInformationTag(tag) || isInstanceUid(tag))
    );
}

/**
 * The rows matching `query` by tag, name or value. A value the table blurs
 * is not searched, or the match count would confirm it one character at a
 * time.
 */
function filterTags(
    tags: DicomTag[],
    query: string,
    showSensitiveInfo: boolean
): DicomTag[] {
    if (!query) {
        return tags;
    }
    const lower = query.toLowerCase();
    return tags.filter(
        (t) =>
            t.tag.toLowerCase().includes(lower) ||
            t.name.toLowerCase().includes(lower) ||
            (!isRedactedTag(t, showSensitiveInfo) &&
                t.value.toLowerCase().includes(lower))
    );
}

function TagToggle({
    tag,
    isExpanded,
    onToggle,
    toggleLabel,
}: {
    tag: DicomTag;
    isExpanded: boolean;
    onToggle: (id: string) => void;
    toggleLabel: string;
}) {
    if (!tag.children) {
        return null;
    }

    const Icon = isExpanded ? ChevronDown : ChevronRight;
    return (
        <IconButton
            type="button"
            size="small"
            aria-expanded={isExpanded}
            aria-label={toggleLabel}
            onClick={(e) => {
                e.stopPropagation();
                onToggle(tag.id);
            }}
            sx={{
                p: 0,
                width: 22,
                height: 22,
                ml: '-4px',
                flexShrink: 0,
                color: 'text.secondary',
                '&:hover': { color: 'text.primary' },
            }}
        >
            <Icon size={14} />
        </IconButton>
    );
}

/**
 * Position of a row in the whole list, not in the rendered window.
 *
 * Only a window of the rows is in the DOM, so assistive technology would
 * otherwise be told the table holds forty rows and that the row under the
 * cursor is the third of them. `aria-rowcount` on the table and this index on
 * each row restore the real size and position. Both are 1-based and count the
 * header, so the first tag is row 2.
 */
function ariaRowIndex(indexInList: number): number {
    return indexInList + 2;
}

/** Groups the elements of one item of a multi-item sequence. */
function SequenceItemRow({
    tag,
    indentPx,
    rowIndex,
}: {
    tag: DicomTag;
    indentPx: number;
    rowIndex: number;
}) {
    return (
        <TableRow aria-rowindex={rowIndex} sx={{ height: TAG_ROW_HEIGHT_PX }}>
            <TableCell
                colSpan={4}
                sx={{ py: 0, px: 0, pl: `${indentPx}px`, border: 'none' }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        pt: 1,
                    }}
                >
                    <SectionLabel sx={separatorLabelSx}>
                        {tag.name}
                    </SectionLabel>
                </Box>
            </TableCell>
        </TableRow>
    );
}

interface TagRowProps {
    tag: DicomTag;
    indentPx: number;
    rowIndex: number;
    isExpanded: boolean;
    isSearching: boolean;
    isSelected: boolean;
    showSensitiveInfo: boolean;
    onToggle: (id: string) => void;
    onSelect: (id: string) => void;
}

/**
 * One tag of the table.
 *
 * Memoized because scrolling replaces one row of the ~40 in the window and
 * leaves the rest at the same `DicomTag` references: without this, every
 * frame re-resolves the `sx` of every cell, which is several milliseconds of
 * a frame's budget.
 */
const TagRow = memo(function TagRow({
    tag,
    indentPx,
    rowIndex,
    isExpanded,
    isSearching,
    isSelected,
    showSensitiveInfo,
    onToggle,
    onSelect,
}: TagRowProps) {
    const intl = useIntl();
    const isSequence = !!tag.children;
    const isIndented = indentPx > 0;
    // Indented rows drop the cell borders in favour of the row's own inset
    // rule, so the rule starts at the indent instead of the table edge.
    const rowCellSx = isIndented ? borderlessCellSx : cellSx;
    const isRedacted = isRedactedTag(tag, showSensitiveInfo);

    return (
        <TableRow
            hover
            selected={isSelected}
            aria-selected={isSelected}
            aria-rowindex={rowIndex}
            onClick={() => {
                onSelect(tag.id);
                if (isSequence) {
                    onToggle(tag.id);
                }
            }}
            // Focus on the row's copy button selects the row, which is how
            // a keyboard reaches the full value.
            onFocus={() => onSelect(tag.id)}
            sx={{
                height: TAG_ROW_HEIGHT_PX,
                cursor: 'pointer',
                ...(isIndented && {
                    position: 'relative',
                    '&::after': {
                        content: '""',
                        position: 'absolute',
                        bottom: 0,
                        left: `${indentPx}px`,
                        right: 0,
                        height: '1px',
                        bgcolor: 'divider',
                    },
                }),
            }}
        >
            <TableCell
                sx={{
                    ...rowCellSx,
                    ...monospaceSx,
                    pl: `${CELL_PADDING_X_PX + indentPx}px`,
                }}
                // The indent is budgeted so that it cannot clip the
                // identifier, but a window narrower than the table's minimum
                // still can, and the identifier is what names the row.
                title={tag.tag}
            >
                <Box display="flex" alignItems="center">
                    {!isSearching && (
                        <TagToggle
                            tag={tag}
                            isExpanded={isExpanded}
                            onToggle={onToggle}
                            toggleLabel={intl.formatMessage({
                                id: isExpanded
                                    ? 'dicom.tagViewer.sequenceCollapse'
                                    : 'dicom.tagViewer.sequenceExpand',
                            })}
                        />
                    )}
                    {tag.tag}
                </Box>
            </TableCell>
            <TableCell sx={{ ...rowCellSx, ...monospaceSx }}>
                {tag.vr}
            </TableCell>
            <TableCell
                sx={{
                    ...rowCellSx,
                    ...(isSequence && { fontWeight: 500 }),
                }}
                // Keyword names run past the column ("ReferencedFrameOf…").
                title={tag.name}
            >
                {tag.name}
            </TableCell>
            <TableCell
                sx={{
                    ...rowCellSx,
                    ...monospaceSx,
                    ...(isRedacted && redactedSx),
                }}
                title={isRedacted ? undefined : tag.value}
            >
                {isRedacted || !hasLiteralValue(tag) ? (
                    tag.value
                ) : (
                    <CopyField copyText={tag.value} fullWidth>
                        <Box sx={clippedValueSx}>{tag.value}</Box>
                    </CopyField>
                )}
            </TableCell>
        </TableRow>
    );
});

/**
 * The DICOM tag table: search, sequence expansion, PHI redaction and row
 * virtualization, with the full value of the selected row below it, the
 * "DICOM Tags" tab of the per-object details dialog.
 *
 * Owns its own search and expansion state and is unmounted when hidden, so
 * reopening it starts from an unfiltered, collapsed tree.
 */
export default function DicomTagBrowser({ dataset }: DicomTagBrowserProps) {
    const intl = useIntl();
    const showSensitiveInfo = useRecoilValue(showSensitiveInfoState);
    const [searchInput, setSearchInput] = useState('');
    const [search] = useDebounceValue(searchInput, SEARCH_DEBOUNCE_MS);
    const [expanded, setExpanded] = useState<Set<string>>(new Set());
    const [selectedId, setSelectedId] = useState<string | null>(null);

    const allTags = useMemo(
        () => (dataset ? extractDicomTags(dataset) : []),
        [dataset]
    );

    const flattenedAllTags = useMemo(() => flattenAllTags(allTags), [allTags]);

    const totalFlatCount = flattenedAllTags.length;

    const selectedTag = useMemo(
        () => flattenedAllTags.find((t) => t.id === selectedId) ?? null,
        [flattenedAllTags, selectedId]
    );

    const isSearching = search.length > 0;

    // Kept apart so that expanding a sequence while a search is active does
    // not re-run the filter over every tag for an unchanged result.
    const searchResults = useMemo(
        () =>
            isSearching
                ? filterTags(flattenedAllTags, search, showSensitiveInfo)
                : [],
        [flattenedAllTags, search, isSearching, showSensitiveInfo]
    );
    const treeRows = useMemo(
        () => (isSearching ? [] : flattenVisibleTags(allTags, expanded)),
        [allTags, expanded, isSearching]
    );
    const visibleTags = isSearching ? searchResults : treeRows;

    const {
        startIndex,
        endIndex,
        paddingTopPx,
        paddingBottomPx,
        scrollRef,
        scrollToTop,
    } = useVirtualRows(visibleTags.length);

    const renderedTags = visibleTags.slice(startIndex, endIndex);

    // A new query produces a different list; keeping the previous offset would
    // drop the user into the middle of the results, or past the end of them.
    // Also re-runs when the container is remounted, which is what re-syncs the
    // window after the dialog is closed and reopened.
    useEffect(() => {
        scrollToTop();
    }, [search, scrollToTop]);

    // The browser mounts with the tags already in view and nothing else to
    // act on, so the caret starts in the search box. preventScroll: the
    // default focus scroll would drag the tag list's scroller back to the
    // top, and the rows are virtualised off its scrollTop.
    const searchRef = useRef<HTMLInputElement>(null);
    useEffect(() => {
        searchRef.current?.focus({ preventScroll: true });
    }, []);

    const handleToggle = useCallback((id: string) => {
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    }, []);

    return (
        <Box display="flex" flexDirection="column" minHeight={0} height="100%">
            <Box
                py={1}
                px={1}
                display="flex"
                alignItems="center"
                gap={2}
                flexShrink={0}
            >
                <TextField
                    size="small"
                    fullWidth
                    placeholder={intl.formatMessage({
                        id: 'dicom.tagViewer.search',
                    })}
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    inputRef={searchRef}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search size={16} />
                            </InputAdornment>
                        ),
                    }}
                />
                {/*
                 * The host supplies the title, so the count sits with the
                 * search box that determines it.
                 */}
                <Typography
                    variant="body2"
                    color="text.secondary"
                    flexShrink={0}
                    whiteSpace="nowrap"
                >
                    {visibleTags.length} / {totalFlatCount}
                </Typography>
            </Box>

            <TableContainer
                ref={scrollRef}
                sx={{
                    flex: 1,
                    overflowX: 'auto',
                    overflowY: 'auto',
                    px: 1,
                }}
            >
                {/*
                 * Fixed layout takes the column widths from the header
                 * row. With virtualization the body rows come and go as
                 * the user scrolls, and an auto layout would resize the
                 * columns to whatever happens to be mounted. The widths
                 * and the minimum are budgeted in `dicomTagLayout`.
                 */}
                <Table
                    size="small"
                    stickyHeader
                    // The header plus every tag, not just the mounted
                    // window (see `ariaRowIndex`).
                    aria-rowcount={visibleTags.length + 1}
                    sx={{
                        tableLayout: 'fixed',
                        minWidth: TABLE_MIN_WIDTH_PX,
                    }}
                >
                    <TableHead>
                        <TableRow aria-rowindex={1}>
                            <TableCell
                                sx={{
                                    ...headerCellSx,
                                    width: TAG_COLUMN_WIDTH_PX,
                                }}
                            >
                                <FormattedMessage id="dicom.tagViewer.tag" />
                            </TableCell>
                            <TableCell
                                sx={{
                                    ...headerCellSx,
                                    width: VR_COLUMN_WIDTH_PX,
                                }}
                            >
                                <FormattedMessage id="dicom.tagViewer.vr" />
                            </TableCell>
                            <TableCell
                                sx={{
                                    ...headerCellSx,
                                    width: NAME_COLUMN_WIDTH_PX,
                                }}
                            >
                                <FormattedMessage id="dicom.tagViewer.name" />
                            </TableCell>
                            {/*
                             * Takes whatever the other three leave, and
                             * never less than MIN_VALUE_COLUMN_WIDTH_PX,
                             * which the table's minimum width reserves for
                             * it. Asking for the whole table is how a
                             * fixed layout is told "the rest": left to
                             * size itself the column is handed a share
                             * that does not fill the row, and declared in
                             * pixels it stops growing with the dialog.
                             */}
                            <TableCell sx={{ ...headerCellSx, width: '100%' }}>
                                <FormattedMessage id="dicom.tagViewer.value" />
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <SpacerRow heightPx={paddingTopPx} />
                        {renderedTags.map((t, i) => {
                            // Search flattens the tree, so its results are
                            // listed without the indent that shows nesting.
                            const indentPx = isSearching
                                ? 0
                                : indentForDepth(t.depth);
                            const isSequenceItem =
                                !t.children && !t.tag && !t.vr;

                            const rowIndex = ariaRowIndex(startIndex + i);

                            return isSequenceItem ? (
                                <SequenceItemRow
                                    key={t.id}
                                    tag={t}
                                    indentPx={indentPx}
                                    rowIndex={rowIndex}
                                />
                            ) : (
                                <TagRow
                                    key={t.id}
                                    tag={t}
                                    indentPx={indentPx}
                                    rowIndex={rowIndex}
                                    isExpanded={expanded.has(t.id)}
                                    isSearching={isSearching}
                                    isSelected={t.id === selectedId}
                                    showSensitiveInfo={showSensitiveInfo}
                                    onToggle={handleToggle}
                                    onSelect={setSelectedId}
                                />
                            );
                        })}
                        <SpacerRow heightPx={paddingBottomPx} />
                        {visibleTags.length === 0 && (
                            <TableRow>
                                <TableCell
                                    colSpan={4}
                                    align="center"
                                    sx={{ py: 4 }}
                                >
                                    <Typography
                                        color="text.secondary"
                                        fontSize={13}
                                    >
                                        {search
                                            ? intl.formatMessage(
                                                  {
                                                      id: 'dicom.tagViewer.noResults',
                                                  },
                                                  { query: search }
                                              )
                                            : intl.formatMessage({
                                                  id: 'dicom.tagViewer.empty',
                                              })}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <DicomTagValueDetail
                tag={selectedTag}
                isRedacted={
                    selectedTag !== null &&
                    isRedactedTag(selectedTag, showSensitiveInfo)
                }
            />
        </Box>
    );
}
